import {
  NeMoTaskType,
  NeMoDatasetFormat,
  NeMoTrainingMethod,
  NeMo26InstanceDetails,
  NeMoInstanceDetails,
} from './types';
import { NEMO_PRETRAINED_MODELS } from './pretrained-models';
import { NEMO_DATASETS } from './datasets';

// Legacy NeMo mock function for backward compatibility
export const mockNeMoInstanceDetails = (taskId: string): NeMoInstanceDetails => ({
  nemoVersion: '2.0.0',
  status: 'active',
  models: [
    {
      name: 'conformer_ctc_large',
      type: 'asr',
      status: 'training',
      checkpoint: 'nvidia/stt_en_conformer_ctc_large',
      metrics: {
        wer: 0.058,
        cer: 0.021,
        epoch: 35,
        totalEpochs: 50,
      },
    },
    {
      name: 'fastpitch_hifigan',
      type: 'tts',
      status: 'inference',
      checkpoint: 'nvidia/tts_en_fastpitch',
      metrics: {},
    },
  ],
  datasetPath: '/workspace/audio_data',
  checkpointsPath: '/workspace/nemo_checkpoints',
  notebookUrl: `http://localhost:8888/lab?token=nemo-${taskId.slice(0, 8)}`,
});

// Mock NeMo 2.6.1 Instance Details function
export const mockNeMo26InstanceDetails = (taskId: string): NeMo26InstanceDetails => {
  const llama8b = NEMO_PRETRAINED_MODELS.find(m => m.id === 'llama-3.1-8b-instruct')!;
  const parakeet = NEMO_PRETRAINED_MODELS.find(m => m.id === 'parakeet-ctc-1.1b')!;
  const fastpitch = NEMO_PRETRAINED_MODELS.find(m => m.id === 'fastpitch-hifigan')!;

  const dollyDataset = NEMO_DATASETS.find(d => d.id === 'dolly-15k')!;
  const librispeechDataset = NEMO_DATASETS.find(d => d.id === 'librispeech-clean')!;
  const ljspeechDataset = NEMO_DATASETS.find(d => d.id === 'ljspeech')!;

  return {
    nemoVersion: '2.6.1',
    status: 'active',
    gpuUtilization: 82,
    gpuMemoryUsed: 65,
    gpuMemoryTotal: 80,
    experiments: [
      {
        id: 'exp-nemo-001',
        name: 'llama8b_customer_support_lora',
        task: 'llm',
        method: 'lora',
        status: 'training',
        pretrainedModel: llama8b,
        dataset: dollyDataset,
        config: {
          method: 'lora',
          epochs: 3,
          batchSize: 4,
          learningRate: 2e-4,
          optimizer: 'adamw',
          scheduler: 'cosine',
          sequenceLength: 4096,
          sequencePacking: true,
          loraConfig: {
            rank: 16,
            alpha: 32,
            dropout: 0.05,
            targetModules: ['q_proj', 'k_proj', 'v_proj', 'o_proj'],
          },
        },
        metrics: {
          currentEpoch: 2,
          totalEpochs: 3,
          trainLoss: 0.8923,
          valLoss: 0.9456,
          perplexity: 2.43,
        },
        progress: 67,
        training: {
          startedAt: Date.now() - 3600000,
          estimatedCompletion: Date.now() + 1800000,
          gpuUtilization: 92,
          gpuMemoryUsed: 14.5,
        },
        exports: [],
        createdAt: Date.now() - 3600000,
        updatedAt: Date.now(),
      },
      {
        id: 'exp-nemo-002',
        name: 'parakeet_medical_transcription',
        task: 'asr',
        method: 'sft',
        status: 'completed',
        pretrainedModel: parakeet,
        dataset: librispeechDataset,
        config: {
          method: 'sft',
          epochs: 50,
          batchSize: 16,
          learningRate: 1e-4,
          optimizer: 'adamw',
          scheduler: 'warmup_cosine',
          specAugment: true,
          speedPerturbation: true,
          decoderType: 'ctc',
        },
        metrics: {
          currentEpoch: 50,
          totalEpochs: 50,
          trainLoss: 0.0234,
          valLoss: 0.0289,
          wer: 0.043,
          cer: 0.018,
        },
        progress: 100,
        training: {
          startedAt: Date.now() - 86400000,
          gpuUtilization: 0,
          gpuMemoryUsed: 0,
        },
        exports: [
          {
            id: 'export-001',
            format: 'nemo',
            status: 'completed',
            path: '/workspace/exports/parakeet_medical.nemo',
            size: '4.2 GB',
            createdAt: Date.now() - 82800000,
          },
          {
            id: 'export-002',
            format: 'onnx',
            status: 'completed',
            path: '/workspace/exports/parakeet_medical.onnx',
            size: '4.4 GB',
            createdAt: Date.now() - 82800000,
          },
        ],
        createdAt: Date.now() - 172800000,
        updatedAt: Date.now() - 82800000,
      },
      {
        id: 'exp-nemo-003',
        name: 'fastpitch_voice_clone',
        task: 'tts',
        method: 'sft',
        status: 'queued',
        pretrainedModel: fastpitch,
        dataset: ljspeechDataset,
        config: {
          method: 'sft',
          epochs: 100,
          batchSize: 32,
          learningRate: 1e-3,
          optimizer: 'adam',
          scheduler: 'warmup_cosine',
          sampleRate: 22050,
          speakerEmbedding: false,
        },
        metrics: {
          currentEpoch: 0,
          totalEpochs: 100,
          trainLoss: 0,
        },
        progress: 0,
        training: {
          startedAt: 0,
          gpuUtilization: 0,
          gpuMemoryUsed: 0,
        },
        exports: [],
        createdAt: Date.now() - 1800000,
        updatedAt: Date.now() - 1800000,
      },
    ],
    datasets: NEMO_DATASETS,
    pretrainedModels: NEMO_PRETRAINED_MODELS,
    workspacePaths: {
      root: '/workspace',
      datasets: '/workspace/data',
      experiments: '/workspace/experiments',
      exports: '/workspace/exports',
      logs: '/workspace/logs',
    },
    notebookUrl: `http://localhost:8888/lab?token=nemo-${taskId.slice(0, 8)}`,
  };
};

// Helper functions for NeMo 2.6.1
export const getNeMoModelsByTask = (task: NeMoTaskType): typeof NEMO_PRETRAINED_MODELS => {
  return NEMO_PRETRAINED_MODELS.filter(model => model.task === task);
};

export const getNeMoDatasetsByTask = (task: NeMoTaskType): typeof NEMO_DATASETS => {
  return NEMO_DATASETS.filter(dataset => dataset.task === task);
};

export const getNeMoDatasetsByFormat = (format: NeMoDatasetFormat): typeof NEMO_DATASETS => {
  return NEMO_DATASETS.filter(dataset => dataset.format === format);
};

export const getTrainingMethodLabel = (method: NeMoTrainingMethod): string => {
  switch (method) {
    case 'full': return 'Full Fine-tuning';
    case 'sft': return 'Supervised Fine-Tuning';
    case 'lora': return 'LoRA';
    case 'qlora': return 'QLoRA (4-bit)';
    case 'ptuning': return 'P-Tuning v2';
  }
};

export const getTrainingMethodDescription = (method: NeMoTrainingMethod): string => {
  switch (method) {
    case 'full': return 'Train all model parameters (highest VRAM, best quality)';
    case 'sft': return 'Supervised fine-tuning on labeled data';
    case 'lora': return 'Low-Rank Adaptation - train small adapter weights (recommended)';
    case 'qlora': return 'Quantized LoRA - 4-bit quantization for memory efficiency';
    case 'ptuning': return 'Prompt tuning with learnable soft prompts';
  }
};
